/**
 * Boards overview (cards 7.1-7.3): the numbers a board tile, the overview
 * toolbar and the recent strip show. Pure — summaries and plain values in,
 * plain values out — so BoardsHome only renders and every rule is testable
 * without mounting anything. The scope chips and text match stay in
 * boardsHomeFilter.js; the in-board task filters are boardFilters.js.
 *
 * Real data only (board decision 11.10): subtasks and pins do not exist, and
 * "last opened" is a local map (boardsUiStore.lastOpened), never synced.
 */
import { isOverdue } from './boardFilters';
import { paletteOf } from './boardLabels';

export const MAX_LABEL_DOTS = 5;
export const RECENT_DAYS = 7;
export const RECENT_MAX = 4;
export const RECENT_MIN = 2;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Done / total / overdue for one board's tasks. `done` is the authoritative
 * "closed" flag (taskStatus.js), so dismissed tasks count as done like the
 * board's own counters; a closed task is never overdue.
 */
export function taskStats(tasks, now = new Date()) {
  let total = 0;
  let done = 0;
  let overdue = 0;
  for (const t of tasks || []) {
    if (!t) continue;
    total += 1;
    if (t.done) done += 1;
    else if (isOverdue(t.dueDate, now)) overdue += 1;
  }
  return { total, done, overdue };
}

/** The colour keys of the board's first labels, for the tile's label dots. */
export function labelDotColors(board, max = MAX_LABEL_DOTS) {
  return paletteOf(board).slice(0, max).map((l) => l.color);
}

/** 0-100, rounded; an empty board is 0 % rather than NaN. */
export function progressPercent(summary) {
  const total = summary?.taskCount || 0;
  if (!total) return 0;
  return Math.round(((summary.doneCount || 0) / total) * 100);
}

const isComplete = (b) => (b?.taskCount || 0) > 0 && (b.doneCount || 0) >= b.taskCount;
const hasOverdue = (b) => (b?.overdueCount || 0) > 0;

/** Counts on the two quick chips, over the boards they would act on. */
export function quickChipCounts(boards) {
  let overdue = 0;
  let completed = 0;
  for (const b of boards || []) {
    if (hasOverdue(b)) overdue += 1;
    if (isComplete(b)) completed += 1;
  }
  return { overdue, completed };
}

/** "Has overdue" keeps boards with an overdue task; "Hide completed" drops
 *  boards whose every task is done. They intersect, like the board filters. */
export function matchesQuick(board, { overdue = false, hideCompleted = false } = {}) {
  if (overdue && !hasOverdue(board)) return false;
  if (hideCompleted && isComplete(board)) return false;
  return true;
}

/** Summary line above the grid: boards, tasks, done, overdue. */
export function overviewTotals(boards) {
  const totals = { boards: 0, tasks: 0, done: 0, overdue: 0 };
  for (const b of boards || []) {
    totals.boards += 1;
    totals.tasks += b.taskCount || 0;
    totals.done += b.doneCount || 0;
    totals.overdue += b.overdueCount || 0;
  }
  return totals;
}

export const SORT_OPENED = 'opened';
export const SORT_OPTIONS = [
  { value: SORT_OPENED, label: 'Sort: Last opened' },
  { value: 'name', label: 'Sort: Name' },
  { value: 'tasks', label: 'Sort: Tasks' },
  { value: 'progress', label: 'Sort: Progress' },
  // Owner, 19.10.2026: "creation based", the board you just made on top.
  { value: 'created', label: 'Sort: Newest first' },
];

export function isSortKey(key) {
  return SORT_OPTIONS.some((o) => o.value === key);
}

/** The local map's key for a board — the id alone, like the board tab key. */
export function openedKey(board) {
  return String(board?.id ?? '');
}

const time = (v) => {
  const n = typeof v === 'number' ? v : Date.parse(v || '');
  return Number.isFinite(n) ? n : 0;
};
const byName = (a, b) =>
  (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }) || openedKey(a).localeCompare(openedKey(b));

/**
 * The grid order. "Last opened": boards opened here first, newest first; the
 * never-opened rest by last change (what the grid did before), ties in the
 * order given. Tasks and
 * progress sort descending like Trello/Linear's "most" sorts; ties by name.
 * "Newest first": by creation date, newest on top; boards with no creation
 * date (made before it was recorded) go last, by last change.
 */
export function sortBoards(boards, sortKey = SORT_OPENED, lastOpened = {}, pinned = null) {
  // Pinned boards first under every sort (Boards Redesign extras §2.2), each
  // group in the chosen order. `pinned` is { boardId: pinnedAt }; without it
  // the order is exactly what it was before pins existed.
  if (pinned && Object.keys(pinned).length) {
    const isPinned = (b) => Object.prototype.hasOwnProperty.call(pinned, openedKey(b));
    const all = boards || [];
    return [
      ...sortBoards(all.filter(isPinned), sortKey, lastOpened),
      ...sortBoards(all.filter((b) => !isPinned(b)), sortKey, lastOpened),
    ];
  }
  const list = [...(boards || [])];
  const opened = (b) => time(lastOpened?.[openedKey(b)]);
  switch (sortKey) {
    case 'name':
      return list.sort(byName);
    case 'tasks':
      return list.sort((a, b) => (b.taskCount || 0) - (a.taskCount || 0) || byName(a, b));
    case 'progress':
      return list.sort((a, b) => progressPercent(b) - progressPercent(a) || byName(a, b));
    case 'created':
      return list.sort((a, b) => time(b.createdAt) - time(a.createdAt) || time(b.updatedAt) - time(a.updatedAt));
    default:
      // Ties keep the incoming order (sort is stable): the tab hands the list
      // in newest-changed first already.
      return list.sort((a, b) => opened(b) - opened(a) || time(b.updatedAt) - time(a.updatedAt));
  }
}

/**
 * The recent strip: up to four boards opened in the last seven days, newest
 * first. Empty when fewer than two qualify — one "recent" board repeats the
 * first tile of the grid and says nothing.
 */
export function recentBoards(boards, lastOpened = {}, now = Date.now(), { days = RECENT_DAYS, max = RECENT_MAX, min = RECENT_MIN } = {}) {
  const cutoff = time(now) - days * DAY_MS;
  const hits = (boards || [])
    .map((b) => ({ board: b, at: time(lastOpened?.[openedKey(b)]) }))
    .filter((e) => e.at > 0 && e.at >= cutoff)
    .sort((a, b) => b.at - a.at)
    .slice(0, max)
    .map((e) => e.board);
  return hits.length >= min ? hits : [];
}

/**
 * "today", "yesterday", "3 d ago", "1 w ago", "5 w ago" — calendar days, so
 * a board opened at 23:50 reads "yesterday" ten minutes later. null when the
 * board was never opened here (the tile shows nothing rather than a guess).
 */
export function formatOpened(at, now = Date.now()) {
  const t = time(at);
  if (!t) return null;
  const startOf = (ms) => {
    const d = new Date(ms);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  const days = Math.max(0, Math.round((startOf(time(now)) - startOf(t)) / DAY_MS));
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} d ago`;
  return `${Math.floor(days / 7)} w ago`;
}
